import React from "react";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import artieImage from "../../assets/images/artie.png";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Recommendation, TacticRecommendation } from "@/types/artie-score";

const TaskDonePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const { taskSelections, recommendations } = (location.state as { taskSelections: any, recommendations: (Recommendation | TacticRecommendation)[] }) || {};

  const selections = taskSelections || {};
  const tasks = recommendations || [];

  // Helper function to get task ID and name for both V2 and legacy
  const getTaskId = (task: Recommendation | TacticRecommendation, index: number) => task.id || `task-${index}`;
  const getTaskName = (task: Recommendation | TacticRecommendation) => {
    // Check if this is a V2 tactic recommendation or legacy recommendation
    const isV2Tactic = 'tactic_id' in task;
    return isV2Tactic
      ? (task as TacticRecommendation).tactic_name
      : (task as Recommendation).what_to_improve;
  };

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">
      <main className="relative z-10 w-full flex flex-col items-center min-h-screen">
        <div className="w-full max-w-5xl px-6 py-12 flex flex-col items-center flex-grow">
          {/* Header & Introduction */}
          <div className="flex flex-col items-center text-center max-w-4xl mb-12">
            {/* Logo */}
            <div className="mb-8 relative">
              <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
              <img
                src={artieImage}
                alt="Artie"
                className="w-48 h-48 object-contain relative z-10"
              />
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
              Thanks for making that <br />
              commitment
            </h1>

            <div className="space-y-1 text-gray-500 text-lg leading-relaxed font-ibm max-w-2xl text-center">
              <p>
                We’re excited to see this project moving toward the marketplace.
                When the marketplace opens, committed projects like yours will
                be first in line. You’re not locked into anything — this just
                helps us focus on projects that are ready to move forward.
              </p>
            </div>
          </div>

          {/* Confirmation Card */}
          <div className="w-full max-w-2xl relative">
            <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm p-8 md:p-12 flex flex-col items-center text-center relative overflow-hidden">
              {/* Checkmark Icon */}
              <div className="w-20 h-20 bg-[#1a1a1a] rounded-full flex items-center justify-center mb-8 shadow-lg">
                <Check className="w-10 h-10 text-white stroke-[3]" />
              </div>

              <h2 className="text-2xl font-bold text-black tracking-tight mb-12">
                Your commitments are saved
              </h2>

              {/* Commitments List */}
              <div className="w-full space-y-6">
                {Object.entries(selections).map(([taskId, selection]) => {
                  const task = tasks.find(t => getTaskId(t, tasks.indexOf(t)) === taskId);
                  return (
                    <div
                      key={taskId}
                      className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0"
                    >
                      <span className="font-bold text-gray-900 text-left">
                        {task ? getTaskName(task) : "Task name"}
                      </span>
                      <span className="text-gray-500 text-right">
                        Assigned to:{" "}
                        <span className="text-gray-900 font-medium">
                          {(selection as any).option === "self"
                            ? "You"
                            : (selection as any).collaborator?.name || "Collaborator"}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer & Navigation */}
        <div className="w-full mt-auto pb-10 px-6 md:px-20">
          <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
          <div className="w-full flex items-center justify-end">
            <Button
              className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all"
              onClick={() => {
                // Mark welcome flow as completed now that user has finished the task flow
                if (projectId) {
                  localStorage.setItem(`welcome-flow-seen-${projectId}`, 'true');
                }
                // Navigate to final project details page
                navigate(`/project-v2/project-details-v2/${projectId}`);
              }}
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TaskDonePage;
